import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { serviceRolesQueryKey } from '../queryKeys'

import { Button, Flex } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import type {
  InternalService,
  InternalServiceRoleHolder
} from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'
import { ChangeServiceRoleModal } from './ChangeServiceRoleModal'
import { GrantServiceRoleModal } from './GrantServiceRoleModal'
import { RevokeServiceRoleModal } from './RevokeServiceRoleModal'
import { ServiceRolesTable } from './ServiceRolesTable'

type RoleModalState =
  | { kind: 'grant' }
  | { kind: 'change'; holder: InternalServiceRoleHolder }
  | { kind: 'revoke'; holder: InternalServiceRoleHolder }
  | null

type ServiceRolesProps = {
  service: InternalService
  canSetRoles: boolean
  /** After a role changes, as what the viewer may do on the service can change with it. */
  onChanged: () => void
}

export function ServiceRoles({ service, canSetRoles, onChanged }: Readonly<ServiceRolesProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const [modal, setModal] = useState<RoleModalState>(null)

  const { data, isLoading, errorMessage, status, refetch } = useQuery(
    serviceRolesQueryKey(service.id),
    internalServiceService.getServiceRoles,
    { serviceId: service.id }
  )
  const holders = data ?? []

  const handleChanged = () => {
    refetch().catch(() => undefined)
    onChanged()
  }

  if (status === 'error' && errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Flex direction="column" gap="md">
      {canSetRoles && (
        <Flex direction="row" justify="flex-end">
          <Button variant="primary" onClick={() => setModal({ kind: 'grant' })} data-testid="grant-service-role-button">
            {t('button.grantRole')}
          </Button>
        </Flex>
      )}
      <ServiceRolesTable
        holders={holders}
        isLoading={isLoading}
        canSetRoles={canSetRoles}
        onChangeRole={holder => setModal({ kind: 'change', holder })}
        onRevoke={holder => setModal({ kind: 'revoke', holder })}
      />
      {modal?.kind === 'grant' && (
        <GrantServiceRoleModal
          service={service}
          heldBy={holders.map(({ holderId }) => holderId)}
          onClose={() => setModal(null)}
          onChanged={handleChanged}
        />
      )}
      {modal?.kind === 'change' && (
        <ChangeServiceRoleModal holder={modal.holder} onClose={() => setModal(null)} onChanged={handleChanged} />
      )}
      {modal?.kind === 'revoke' && (
        <RevokeServiceRoleModal
          holder={modal.holder}
          serviceName={service.name}
          onClose={() => setModal(null)}
          onChanged={handleChanged}
        />
      )}
    </Flex>
  )
}
